import { useState, useRef } from 'react';
import { Tooltip, Badge, Divider, Menu, MenuItem, TextField, InputAdornment } from '@mui/material';
import {
    Toolbar,
    ToolbarButton,
    ColumnsPanelTrigger,
    FilterPanelTrigger,
    ExportExcel,
    ExportCsv,
    PivotPanelTrigger,
    QuickFilter,
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger
} from '@mui/x-data-grid-premium';
import { styled } from '@mui/material/styles';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';

const StyledQuickFilter = styled(QuickFilter)({
    display: 'grid',
    alignItems: 'center'
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity'])
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    overflowX: 'clip',
    width: ownerState.expanded ? 260 : 'var(--trigger-width)',
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(['width', 'opacity'])
}));

export default function CustomToolbar({ children, ...props }) {
    
    const { elements, csvOptions, excelOptions } = props;
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuTriggerRef = useRef(null);

    return (
        <Toolbar className="toolbar-justify-space-between">
            <div className="left-toolbar">{children}</div>

            <div className="right-toolbar">
                {elements?.includes('columns') && (
                    <Tooltip title="Colonne">
                        <ColumnsPanelTrigger render={<ToolbarButton />}>
                            <ViewColumnIcon fontSize="small" />
                        </ColumnsPanelTrigger>
                    </Tooltip>
                )}

                {elements?.includes('filters') && (
                    <Tooltip title="Filtri">
                        <FilterPanelTrigger
                            render={(props, state) => (
                                <ToolbarButton {...props} color="default">
                                    <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                                        <FilterListIcon fontSize="small" />
                                    </Badge>
                                </ToolbarButton>
                            )}
                        />
                    </Tooltip>
                )}

                {elements?.includes('pivot') && (
                    <Tooltip title="Pivot">
                        <PivotPanelTrigger
                            render={(triggerProps, state) => (
                                <ToolbarButton {...triggerProps} color={state.active ? 'primary' : 'default'} />
                            )}
                        >
                            <PivotTableChartIcon fontSize="small" />
                        </PivotPanelTrigger>
                    </Tooltip>
                )}

                {elements?.includes('export') && (
                    <>
                        <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                        <Tooltip title="Esporta">
                            <ToolbarButton
                                ref={exportMenuTriggerRef}
                                id="export-menu-trigger"
                                aria-controls="export-menu"
                                aria-haspopup="true"
                                aria-expanded={exportMenuOpen ? 'true' : undefined}
                                onClick={() => setExportMenuOpen(true)}
                            >
                                <FileDownloadIcon fontSize="small" />
                            </ToolbarButton>
                        </Tooltip>
                        <Menu
                            id="export-menu"
                            anchorEl={exportMenuTriggerRef.current}
                            open={exportMenuOpen}
                            onClose={() => setExportMenuOpen(false)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{
                                list: {
                                    'aria-labelledby': 'export-menu-trigger'
                                }
                            }}
                        >
                            <ExportCsv options={csvOptions} render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                                Esporta in CSV
                            </ExportCsv>
                            <ExportExcel options={excelOptions} render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                                Scarica come Excel
                            </ExportExcel>
                        </Menu>
                    </>
                )}

                {elements?.includes('quickFilter') && (
                    <>
                        <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                        <StyledQuickFilter>
                            <QuickFilterTrigger
                                render={(triggerProps, state) => (
                                    <Tooltip title="Cerca" enterDelay={0}>
                                        <StyledToolbarButton
                                            {...triggerProps}
                                            ownerState={{ expanded: state.expanded }}
                                            color="default"
                                            aria-disabled={state.expanded}
                                        >
                                            <SearchIcon fontSize="small" />
                                        </StyledToolbarButton>
                                    </Tooltip>
                                )}
                            />
                            <QuickFilterControl
                                render={({ ref, ...controlProps }, state) => (
                                    <StyledTextField
                                        {...controlProps}
                                        ownerState={{ expanded: state.expanded }}
                                        inputRef={ref}
                                        aria-label="Cerca"
                                        placeholder="Cerca..."
                                        size="small"
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: state.value ? (
                                                    <InputAdornment position="end">
                                                        <QuickFilterClear
                                                            edge="end"
                                                            size="small"
                                                            aria-label="Clear search"
                                                            material={{ sx: { marginRight: -0.75 } }}
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </QuickFilterClear>
                                                    </InputAdornment>
                                                ) : null,
                                                ...controlProps.slotProps?.input
                                            },
                                            ...controlProps.slotProps
                                        }}
                                    />
                                )}
                            />
                        </StyledQuickFilter>
                    </>
                )}
            </div>
        </Toolbar>
    );
}
